import React from 'react';
import I18n from 'i18n-js';
import { get } from 'lodash';
import qs from 'qs';

import bind from '@reverbdotcom/commons/src/bind';

import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import { RCStarRatingSelector, RCAlertBox } from '@reverbdotcom/cadence/components';

import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ProductReviewInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';

const PHRASE_FLAGS = [
  'seller',
  'sold',
  'shipped',
  'ship',
  'shipment',
  'shipping',
  'arrived',
  'transaction',
  'delivery',
  'refund',
  'damaged',
  'returned',
  'condition',
  'deal',
  'money back',
  'offer',
  'do business',
  'doing business',
  'fraud',
  'fraudulent',
  'scam',
  'as described',
  'fast shipping',
  'thank you',
  'description',
];

interface CspReviewErrors {
  rating?: string;
  body?: string;
}

const PHRASE_FLAGS_REGEX = new RegExp(`\\b(${PHRASE_FLAGS.join('|')})\\b`, 'gi');

interface IProps {
  onSubmit: any;
  myReview?: any;
  productTitle: string;
  productImage: string;
  cspId?: string;
  trackingDisplayStyle?: 'product' | 'csp';
}

interface IState {
  modalOpen: boolean;
  starsSelected: number;
  review: string;
  title: string;
  errors: CspReviewErrors;
}

class CSPReviewButton extends React.Component<IProps & IUserContext, IState> {
  state = {
    modalOpen: false,
    starsSelected: get(this.props.myReview, 'rating', null),
    title: get(this.props.myReview, 'title', ''),
    review: get(this.props.myReview, 'body', ''),
    errors: {},
  };

  componentDidMount() {
    const show_review_param = qs.parse(
      window.location.search,
      { ignoreQueryPrefix: true },
    )['show-review-form'];

    this.setState({ modalOpen: !!show_review_param });
  }

  @bind
  openModal() {
    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.StartReview,
      displayStyle: this.props.trackingDisplayStyle,
      cspId: this.props.cspId,
    });
    this.setState({ modalOpen: true });
  }

  @bind
  closeModal() {
    this.setState({ modalOpen: false });
  }

  @bind
  submitReview() {
    const data = {
      rating: this.state.starsSelected,
      body: this.state.review,
      title: this.state.title,
    };

    const req = this.props.onSubmit(data);

    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.SubmitReview,
      displayStyle: this.props.trackingDisplayStyle,
      cspId: this.props.cspId,
    });

    req.then(() => {
      this.setState({ errors: {}, modalOpen: false });
    });

    req.catch((e) => {
      this.setState({ errors: this.formatErrors(e) });
    });
  }

  @bind
  starRatingSelected(starsSelected) {
    this.setState({ starsSelected });
  }

  @bind
  updateField(val) {
    this.setState(val);
  }

  formatErrors(e): CspReviewErrors {
    const errorObject = {} as CspReviewErrors;

    if (e.data.errors.rating) { errorObject.rating = I18n.t('discovery.cspReviewButton.errors.rating'); }
    if (e.data.errors.body) { errorObject.body = I18n.t('discovery.cspReviewButton.errors.body'); }

    return errorObject;
  }

  shouldDisplayWarning() {
    return this.state.review.length && this.state.review.match(PHRASE_FLAGS_REGEX);
  }

  buttonText() {
    if (this.props.myReview) return I18n.t('discovery.cspReviewButton.editYourReview');
    return I18n.t('discovery.cspReviewButton.writeAReview');
  }

  modalTitle() {
    if (this.props.myReview) return I18n.t('discovery.cspReviewButton.editYourReview');
    return I18n.t('discovery.cspReviewButton.modalTitle');
  }

  renderWarning() {
    if (!this.shouldDisplayWarning()) return;

    return (
      <div className="scaling-mb-2">
        <RCAlertBox type="error">
          {I18n.t('discovery.cspReviewButton.reviewWarning')}
        </RCAlertBox>
      </div>
    );
  }

  renderModal() {
    if (this.props.user.loggedOut) {
      return (
        <SignupSigninModal
          onRequestClose={this.closeModal}
          active={this.state.modalOpen}
          message={I18n.t('discovery.cspReviewButton.authRequired')}
          trackSource="CSPReviewButton"
        />
      );
    }

    return (
      <ModalDialog
        isOpen={this.state.modalOpen}
        headerTitle={this.modalTitle()}
        onSubmit={this.submitReview}
        saveButtonText={I18n.t('discovery.cspReviewButton.postReview')}
        onRequestClose={this.closeModal}
      >
        <h4 className="weight-bold mb-half">
          {I18n.t('discovery.cspReviewButton.youAreReviewing')}
        </h4>
        <div className="d-flex fx-align-center scaling-mb-2">
          {this.props.productImage &&
            <div className="fx-width-10 mr-1">
              <img
                className="width-100 d-block bd-1 bd-primary"
                src={this.props.productImage}
                alt={this.props.productTitle}
              />
            </div>
          }
          <h4>
            {this.props.productTitle}
          </h4>
        </div>
        <RCStarRatingSelector
          onSelect={this.starRatingSelected}
          rating={this.state.starsSelected}
          inputName="csp-review-rating"
          inputId="csp-review-rating"
          label={I18n.t('discovery.cspReviewButton.yourRating')}
          errorText={get(this.state.errors, 'rating')}
        />
        <FormGroupWithInput
          inputName="title"
          label={I18n.t('discovery.cspReviewButton.reviewTitle')}
          updateField={this.updateField}
          value={this.state.title}
          tagOptional
        />
        <FormGroupWithInput
          inputName="review"
          label={I18n.t('discovery.cspReviewButton.yourReview')}
          inputType="textarea"
          updateField={this.updateField}
          value={this.state.review}
          errorText={get(this.state.errors, 'body')}
        />
        {this.renderWarning()}
        <div>
          <h4 className="size-110 weight-bold">
            {I18n.t('discovery.cspReviewButton.reviewTips')}
            <small className="ml-space">
              (
              <a
                href="https://reverb.com/page/product-review-guidelines"
                rel="nofollow noopener noreferrer"
                target="_blank"
              >
                {I18n.t('discovery.cspReviewButton.seeAll')}
              </a>
              )
            </small>
          </h4>
          <div className="size-80 d-flex fx-align-center mtb-1">
            <i className="fa fa-check color-green mr-1" />
            {I18n.t('discovery.cspReviewButton.do')}
          </div>
          <div className="size-80 d-flex fx-align-center">
            <i className="fa fa-close color-red mr-1" />
            <div>
              {I18n.t('discovery.cspReviewButton.dont')}
            </div>
          </div>
        </div>
      </ModalDialog>
    );
  }

  render() {
    if (this.props.myReview && this.props.myReview.blocked) {
      return null;
    }

    return (
      <div>
        <button
          onClick={this.openModal}
          className="csp-review-button"
        >
          {this.buttonText()}
        </button>
        {this.renderModal()}
      </div>
    );
  }
}

export default withUserContext(CSPReviewButton);
